import React, { useEffect, useRef, useState,memo, useCallback } from 'react';
import { isSet,isObject, getShortenedVisitCategoryName } from '../../utils/commonUtils';
import { calendarWidgetVisitTypeBasedColor } from './calendarFunctions';
import Buttons from '../UI/Buttons';
import VisitsService from '../../services/VisitsService';
import ProviderService from '../../services/ProviderService';
import FormInputs from '../UI/FormInputs';
import WithRoutify from '../../hoc/WithRoutify';
import Icons from '../UI/Icons';
import Button from '../UI/Buttons/Button';
import Acl from '../../hoc/Acl';
import PERMISSIONS from '../../utils/AclPermissions';
import withReduxState from '../../hoc/wReduxState';
import { acl_check } from '../../utils/aclUtils';
import { useNavigate } from 'react-router-dom';
import { isWorkCompVisitCategory } from '../../utils/visitUtils';
import TranscriptionRecordingModal from '../../containers/EmployeeVisitContainer/NewUIVisitList/TranscriptionRecordingModal';
import {
  getVoiceDocProgressFromSources,
  getVoiceToolbarUi,
  isVoiceTranscriptionListCategory,
} from '../../utils/transcriptionUtils';

const VisitQuickViewPopup = ({ position, modalToogle, isShow, quickViewDetails, showQuickVisitView, ...props }) => {
    console.log('quickViewDetails',quickViewDetails);
    const data = quickViewDetails;
    const popupRef = useRef(null);
    const [validation , setValidation] = useState({});
    let hasEditAccess = acl_check(props.profile.permissions,PERMISSIONS.VISITS.EDIT);

    const [visitStatus, setVisitStatus] = useState(data?.visit_status);
    const [primaryProviderId, setPrimaryProviderId] = useState(data?.primary_provider_id || '');
    const [providerOptions, setProviderOptions] = useState([]);
    const [loadingProviders, setLoadingProviders] = useState(false);
    const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
    const [transcriptionVisitId, setTranscriptionVisitId] = useState(null);
    const [transcriptionVisitData, setTranscriptionVisitData] = useState(null);
    const [voiceUi, setVoiceUi] = useState(() => getVoiceToolbarUi(null));

    // Update state when data changes
    useEffect(() => {
        if (data?.visit_status) {
            setVisitStatus(data.visit_status);
        }
        if (data?.primary_provider_id) {
            setPrimaryProviderId(data.primary_provider_id);
        }
    }, [data]);

    const handleClickOutside = useCallback((event) => {
        if (popupRef.current && !popupRef.current.contains(event.target)) {
            modalToogle(); // Close the popup if clicked outside
        }
    }, [modalToogle]);

    useEffect(()=>{
       setValidation({});
    },[visitStatus, primaryProviderId]);

    // Fetch providers when component loads or clinic_id changes
    useEffect(() => {
        const fetchProviders = async () => {
            // Try to get clinic_id from employee.clinic_id or employee.clinic.clinic_id
            const clinicId = data?.employee?.clinic_id || data?.employee?.clinic?.clinic_id;
            if (!clinicId) {
                setProviderOptions([]);
                return;
            }
            
            setLoadingProviders(true);
            try {
                const providers = await ProviderService.getProviderByClinicId(clinicId, {});
                const options = isSet(providers, []).map((item) => ({
                    label: `${isSet(item.first_name, "")} ${isSet(item.last_name, "")}`.trim(),
                    value: item.provider_id,
                }));
                
                // If current provider is not in the list, add it
                if (data?.primary_provider && data?.primary_provider_id) {
                    const currentProviderName = `${data.primary_provider.first_name} ${data.primary_provider.last_name}`.trim();
                    const providerExists = options.some(opt => opt.value === data.primary_provider_id);
                    if (!providerExists) {
                        options.unshift({
                            label: currentProviderName,
                            value: data.primary_provider_id,
                        });
                    }
                }
                
                setProviderOptions(options);
            } catch (e) {
                console.error('Error fetching providers:', e);
                setProviderOptions([]);
            } finally {
                setLoadingProviders(false);
            }
        };

        if (isShow) {
            fetchProviders();
        }
    }, [isShow, data?.employee?.clinic_id, data?.employee?.clinic?.clinic_id, data?.primary_provider_id]);

    useEffect(() => {
        // Attach the event listener when the component mounts
        document.addEventListener('mousedown', handleClickOutside);

        // Detach the event listener when the component unmounts
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]);

    const dateParts = data?.visit_date.split("/");
    const month = parseInt(dateParts[0], 10) - 1; // Months are zero-based in JavaScript Date
    const day = parseInt(dateParts[1], 10);
    const year = parseInt(dateParts[2], 10);
    const date = new Date(year, month, day);

    // Format the date to the desired format
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const formattedDate = date.toLocaleDateString('en-US', options);
    const navigate = useNavigate();

    /** Align with whiteboard / visit list: screen_type wins, then visit_category */
    const categoryForRedirect = data?.screen_type || data?.visit_category;
    const canStartTranscription = isVoiceTranscriptionListCategory(categoryForRedirect);

    useEffect(() => {
        let active = true;
        const loadVoiceProgress = async () => {
            const visitId = data?.visit_id;
            if (!visitId) {
                if (active) setVoiceUi(getVoiceToolbarUi(null));
                return;
            }
            try {
                const audioRows = await VisitsService.getAudioList(String(visitId));
                const transcriptSegments = Array.isArray(audioRows)
                    ? audioRows
                          .filter(
                              (r) =>
                                  String(r?.status || '').toLowerCase() === 'completed' &&
                                  String(r?.transcript_text || '').trim() !== ''
                          )
                          .map((r) => ({ text: r.transcript_text }))
                    : [];
                const rows = await VisitsService.getAiNotes(String(visitId));
                const merged = Array.isArray(rows) ? rows.find((r) => r.is_merged_note) : null;
                const detail = merged?.id ? await VisitsService.getAiNoteById(merged.id) : null;
                const progress = getVoiceDocProgressFromSources(detail?.notes_collection, transcriptSegments);
                if (active) setVoiceUi(getVoiceToolbarUi(progress));
            } catch (e) {
                if (active) setVoiceUi(getVoiceToolbarUi(null));
            }
        };
        if (isShow) {
            loadVoiceProgress();
        }
        return () => {
            active = false;
        };
    }, [isShow, data?.visit_id]);

    const handleClick = () => {
        const hrefState = { currentDay:props.currentDay}; 
        let href = isWorkCompVisitCategory(categoryForRedirect) ?
            `/visits/emr-details/${quickViewDetails.visit_id}/appointments`
            :
            `/visits/visit-details/${quickViewDetails.visit_id}/appointments`

        navigate(href,{state:hrefState}); //navigate to visit page with backto params
    };

    /**
     * Purpose: Opens the voice-first transcription modal for the quick-view visit
     * Inputs: none (uses `data` / quickViewDetails from closure)
     * Output: void
     */
    const openTranscriptionWorkflow = useCallback(() => {
        const visitId = data?.visit_id;
        if (!visitId) return;
        setTranscriptionVisitId(visitId);
        setTranscriptionVisitData(data);
        setShowTranscriptionModal(true);
    }, [data]);

    /**
     * Purpose: Closes transcription modal and clears visit binding
     * Inputs: none
     * Output: void
     */
    const closeTranscriptionModal = useCallback(() => {
        setShowTranscriptionModal(false);
        setTranscriptionVisitId(null);
        setTranscriptionVisitData(null);
    }, []);

    const headerButtons = [
        {
            className: 'btn btn-icon btn-transparent-dark',
            iconType: voiceUi.iconType,
            iconSize: 20,
            label: '',
            title: canStartTranscription
                ? voiceUi.title
                : 'Transcription Not Available.',
            acl: PERMISSIONS.VISITS.VIEW_CHART,
            disabled: !canStartTranscription,
            clickHandler: () => {
                if (!canStartTranscription) return;
                openTranscriptionWorkflow();
            },
        },
        {
            className: "btn btn-icon btn-transparent-dark",
            iconType: "Edit",
            iconColor: '#5f6368',
            label: "",
            acl: "appointment-detail_view",
            accessType: "Hide",
            title: "Detail view",
            clickHandler: () => {
                modalToogle();
                props.viewVisitHandler(data.visit_id, data.snapClinicConsentForm, data.employee, data.snapVisitConsentForm);
            }
        },
        {
            className: "btn btn-icon btn-transparent-dark",
            iconType: "Close",
            label: "",
            acl: "any",
            title: "Cancel",
            clickHandler: modalToogle
        }
    ];

    const updateVisitHandler = async () => {
        const { Toast, Loader } = props;
        let updateValidation = {...validation}
        const payload = {
            visit_date: data?.visit_date,
            visit_time: data?.visit_time,
            visit_category: data?.visit_category,
            employee_id: data?.employee_id,
            visit_status: visitStatus,
            primary_provider_id: primaryProviderId || null
        };
        Loader(true);
        try {
            const response = await VisitsService.updateVisits(quickViewDetails.visit_id, payload);
            if (response.status && response.status === "ERROR") {
                if (isObject(response.data)) {
                  for (let key in response.data) {
                    updateValidation = {
                      ...updateValidation,
                      [key]: response.data[key].join(","),
                    };
                  }
                } else {
                  Toast.error(isSet(response.data,'Something went wrong..'));
                  Loader(false);
                }
                
                if (Object.keys(updateValidation).length > 0) {
                  setValidation(updateValidation);
                  Loader(false);
                  return;
                }
            } else {
                props.calendarActionHandler();
                Toast.success('Visit Updated Successfully');
                modalToogle(); 
                Loader(false);
            }
        } catch (e) {
            console.error(e);
            Loader(false);
        }
    };

    return (
        <>
        <div
            ref={popupRef}
            className='calendarBox'
            id="calendarQuickViewPopup"
            style={{
                left: position?.left,
                zIndex: 999,
                position: 'absolute',
                top: position?.top,
                display: isShow ? 'block' : 'none',
            }}
        >
            <div className="card border-0 shadow-lg" style={{ width: '340px' }}>
                <div className="card-header">
                    <div className="addAction d-flex justify-content-between align-items-center">
                        <div className="card-title mb-0 text-truncate min-w-0 flex-grow-1 pe-2">
                            <h6 className="text-md mb-0 font-weight-bold text-truncate" title={`${data?.employee?.first_name} ${data?.employee?.last_name}`}>
                                {data?.employee?.first_name} {data?.employee?.last_name}
                            </h6>
                        </div>
                        <div className="d-flex align-items-center justify-content-end flex-nowrap flex-shrink-0 gap-1 visit-quick-view-header-btns">
                            <Buttons type='BtnList' buttonList={headerButtons} />
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-1">
                            <Icons type={'Dot'} className={calendarWidgetVisitTypeBasedColor(data?.visit_category, 'TEXT', data?.visit_status)} />
                        </div>
                        <div className="col-10">
                            <p className={`text-md font-weight-bold mb-1 ${calendarWidgetVisitTypeBasedColor(data?.visit_category, 'TEXT', data?.visit_status)}`}>
                                {data?.visit_category&&getShortenedVisitCategoryName(data?.visit_category)}
                            </p>
                            <p className="text-md mb-1">{formattedDate}</p>
                            <p className="text-md mb-1">{data?.visit_time}</p>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-1" />
                        <div className="col-11">
                            <div className="form-group mt-3 mb-0">
                                <label className="text-sm text-truncate mb-0 opacity-75">
                                    Provider
                                </label>
                                <div>
                                    <FormInputs
                                        readOnly={!hasEditAccess}
                                        type='SelectList'
                                        className="form-control form-control-solid"
                                        name="primary_provider_id"
                                        value={primaryProviderId}
                                        id="primary_provider_id"
                                        options={[
                                            { label: 'No Provider', value: '' },
                                            ...providerOptions
                                        ]}
                                        changeHandler={setPrimaryProviderId}
                                        disabled={loadingProviders || (!data?.employee?.clinic_id && !data?.employee?.clinic?.clinic_id)}
                                    />
                                    {isSet(validation['primary_provider_id'], "") !== "" ? (
                                        <span className="text-danger m-1">
                                        {isSet(validation['primary_provider_id'], "")}
                                        </span>
                                    ) : ("")}
                                </div>
                            </div>
                            <div className="form-group mt-3 mb-0">
                                <label className="text-sm text-truncate mb-0 opacity-75">
                                    Visit Status
                                </label>
                                <div>
                                    <FormInputs
                                        readOnly={!hasEditAccess}
                                        type='SelectList'
                                        className="form-control form-control-solid"
                                        name="visit_status"
                                        value={visitStatus}
                                        id="visit_status"
                                        options={props.visitStatusOption}
                                        changeHandler={setVisitStatus}
                                    />
                                    {isSet(validation['visit_status'], "") !== "" ? (
                                        <span className="text-danger m-1">
                                        {isSet(validation['visit_status'], "")}
                                        </span>
                                    ) : ("")}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
               
                    <Acl acl={PERMISSIONS.VISITS.EDIT}>
                    <div className="card-footer d-flex justify-content-center">
                        <Button 
                        clickHandler={updateVisitHandler}
                        acl="appointment-update_visit"
                        accessType="Hide"
                        className="btn btn-primary text-sm" label="Update" title="Update Visit" 
                        />
                        <Button
                            clickHandler={handleClick}
                            acl="appointment-chart_visit"
                            accessType="Hide"
                            className="btn btn-outline-primary text-sm mx-2" label="Chart Visit"
                        />
                          </div>                                        
                    </Acl>
                    {/* <button onClick={updateVisitStatusHandler} className="btn btn-primary text-sm" type="button">Update</button> */}
              
            </div>
        </div>
        <TranscriptionRecordingModal
            isOpen={showTranscriptionModal && !!transcriptionVisitId}
            visitId={transcriptionVisitId}
            visitData={transcriptionVisitData}
            profile={props.profile}
            onClose={closeTranscriptionModal}
            Toast={props.Toast}
            Loader={props.Loader}
        />
        </>
    );
};

VisitQuickViewPopup.defaultProps = {
    position: {
        x: 0,
        y: 0
    }
};

export default withReduxState(WithRoutify(memo(VisitQuickViewPopup)));
